import React, { useEffect, useState } from "react";
import { ChevronUp, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEndCallMutation } from "@/store/rest_api";
import { useCallDialog } from "@/context/CallDialogContext";

interface MiniCallDockProps {
  onExpand: () => void;
}

export default function MiniCallDock({ onExpand }: MiniCallDockProps) {
  const { activeCall, activeCallInfo } = useCallDialog();
  const [endCall] = useEndCallMutation();
  const startedAt = activeCallInfo?.startedAt ?? Date.now();
  const [elapsed, setElapsed] = useState(() => Math.floor((Date.now() - startedAt) / 1000));

  const formatElapsed = (s: number) => {
    const m = Math.floor(s / 60)
      .toString()
      .padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  const handleHangUp = async () => {
    try {
      activeCall?.disconnect();
    } catch (e) {
      console.error("Twilio disconnect error:", e);
    }

    const idToUse = activeCallInfo?.callId || activeCall?.parameters?.CallSid;
    if (idToUse) {
      try {
        await endCall(idToUse).unwrap();
      } catch (e) {
        console.error("End call API error:", e);
      }
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [startedAt]);

  return (
    <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2.5 rounded-full border bg-background py-1.5 pl-4 pr-1.5 shadow-2xl">
      <span className="h-2 w-2 rounded-full animate-pulse shrink-0" style={{ backgroundColor: "var(--success)" }} />
      <span className="text-sm font-semibold max-w-[140px] truncate dark:text-white">
        {activeCallInfo?.displayName ?? "On call"}
      </span>
      <span className="text-sm font-semibold tabular-nums" style={{ color: "var(--success)" }}>
        {formatElapsed(elapsed)}
      </span>
      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8 rounded-full"
        onClick={onExpand}
        title="Expand"
      >
        <ChevronUp className="h-4 w-4" />
      </Button>
      <Button
        size="icon"
        className="h-8 w-8 rounded-full bg-red-600 hover:bg-red-700 text-white"
        onClick={handleHangUp}
        title="Hang up"
      >
        <Phone className="h-4 w-4 rotate-135" />
      </Button>
    </div>
  );
}
